/**
 * Generic field-value validators shared across the app (signature send form,
 * contact dialog, …). Kept dependency-free (only phone parsing) so any feature
 * can reuse them without pulling in signature-specific types.
 *
 * Empty values are NOT validated here — callers decide required-ness. These
 * only answer "is this non-empty value well-formed for its type?".
 */
import { parsePhoneNumber } from "./Haim/phoneNumbersUtils";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NUMBER_RE = /^-?\d+(\.\d+)?$/;

export const isValidEmail = (s: string): boolean => EMAIL_RE.test(s.trim());
export const isValidNumber = (s: string): boolean => NUMBER_RE.test(s.trim());
export const isValidPhone = (s: string): boolean => parsePhoneNumber(s.trim()) !== null;

/**
 * Strict phone check for USER-TYPED input (the contact modal). parsePhoneNumber
 * is deliberately lenient — isPossible() only, so stored/scraped numbers keep
 * parsing (see phoneNumbersUtils) — but that lets wrong-length numbers through
 * (isPossible accepts 9- and 11-digit "Israeli mobiles"). For new input we
 * require either:
 *  - a fully VALID number per country metadata (exact length + allocated
 *    prefix — covers landlines and foreign numbers), or
 *  - the exact Israeli mobile shape 05X-XXXXXXX (a 9-digit national number
 *    starting with 5) — accepts MVNO prefixes (055-*) that the bundled
 *    metadata may lag on, while still enforcing the exact length.
 */
export const isStrictValidPhone = (s: string): boolean => {
  const parsed = parsePhoneNumber(s.trim());
  if (!parsed) return false;
  if (parsed.isValid()) return true;
  return parsed.country === "IL" && /^5\d{8}$/.test(String(parsed.nationalNumber));
};

/**
 * Israeli National ID (תעודת זהות) — Luhn-variant checksum on a 9-digit
 * number. Shorter inputs are zero-padded on the left, matching how older
 * IDs are commonly typed without leading zeros. A bare digit count check
 * isn't enough — most 9-digit strings would pass — so the checksum is the
 * actual gate.
 */
export const isValidIsraeliId = (s: string): boolean => {
  const digits = s.replace(/\D/g, "");
  if (digits.length < 5 || digits.length > 9) return false;
  const padded = digits.padStart(9, "0");
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    let n = +padded[i] * ((i % 2) + 1);
    if (n > 9) n = ((n / 10) | 0) + (n % 10);
    sum += n;
  }
  return sum % 10 === 0;
};
